import {
  getStartupPositionAnalysis,
  postStartupPositionAnalyze,
} from 'apis/request'
import { requestToast } from 'components/common/toast'
import { IS_BROWSER, POSITION_ANALYSIS_STORAGE_KEY } from 'infra/constants'
import { makeAutoObservable } from 'mobx'
import { makePersistable } from 'mobx-persist-store'
import { mutate } from 'swr'
import { AnalysisStatusCode } from 'types/common'

type PositionAnalysisStep = 'select' | 'upload' | 'signup'
export interface StartupPositionForAnalysisParams {
  id: number
  name: string
  startup: {
    id: number
    name: string
    thumbnail: string
  }
}
export class PositionAnalysisStore {
  selectedPosition: StartupPositionForAnalysisParams | null = null
  isOpenAnalysisModal: boolean = false
  analysisModalStep: PositionAnalysisStep = 'select'
  analysisList: {
    [positionId: number]: StartupPositionForAnalysisParams & {
      isLoading: boolean
      loadedAt: string | null
    }
  } = {}
  analysisIntervals: { [positionId: number]: ReturnType<typeof setInterval> } =
    {}
  guestAnalysisCount: number = 0

  constructor() {
    makePersistable(this, {
      name: POSITION_ANALYSIS_STORAGE_KEY,
      properties: ['analysisList', 'guestAnalysisCount'],
      storage: IS_BROWSER ? localStorage : undefined,
    })
    makeAutoObservable(this)
  }

  get sortedAnalysisList() {
    return Object.values(this.analysisList)
      .sort((a, b) => {
        const aTime = new Date(a.loadedAt ?? 0).getTime()
        const bTime = new Date(b.loadedAt ?? 0).getTime()
        return bTime - aTime
      })
      .filter((item) => !item.isLoading)
  }

  reset() {
    this.selectedPosition = null
    this.isOpenAnalysisModal = false
    this.analysisModalStep = 'select'
    this.analysisList = {}
    this.analysisIntervals = {}
    this.guestAnalysisCount = 0
  }

  addGuestAnalysisCount() {
    this.guestAnalysisCount++
  }

  resetGuestAnalysisCount() {
    this.guestAnalysisCount = 0
  }

  getAnalysisItem(key: string | number) {
    return this.analysisList[Number(key)]
  }

  openAnalysisModal(
    position: StartupPositionForAnalysisParams,
    step: PositionAnalysisStep = 'select',
  ) {
    this.isOpenAnalysisModal = true
    this.selectedPosition = position
    this.analysisModalStep = step
  }

  openAnalysisModalForSignup() {
    this.isOpenAnalysisModal = true
    this.analysisModalStep = 'signup'
  }

  closeAnalysisModal() {
    this.isOpenAnalysisModal = false
    this.selectedPosition = null
    this.analysisModalStep = 'select'
  }

  addAnalysis(position: StartupPositionForAnalysisParams) {
    this.analysisList[position.id] = {
      id: position.id,
      name: position.name,
      startup: {
        id: position.startup.id,
        name: position.startup.name,
        thumbnail: position.startup.thumbnail,
      },
      isLoading: true,
      loadedAt: null,
    }
  }

  setAnalysisModalStep(step: PositionAnalysisStep) {
    this.analysisModalStep = step
  }

  removeAnalysis(positionId: number | string) {
    delete this.analysisList[Number(positionId)]
  }

  addAnalysisInterval(position: StartupPositionForAnalysisParams) {
    const intervalId = setInterval(async () => {
      try {
        const { status } = await getStartupPositionAnalysis(position.id)
        if (status === AnalysisStatusCode.COMPLETED) {
          await mutate(`/startup-positions/${position.id}/analysis`)
          this.analysisList[position.id] = {
            ...this.analysisList[position.id],
            isLoading: false,
            loadedAt: new Date().toISOString(),
          }
          this.removeAnalysisInterval(position.id)
        }
        if (status === AnalysisStatusCode.FAILED) {
          this.handleError(position.id)
        }
      } catch (e) {
        this.handleError(position.id)
      }
    }, 10000)
    this.analysisIntervals[position.id] = intervalId
  }

  async handleError(positionId: number) {
    const position = { ...this.analysisList[positionId] }
    await mutate(`/startup-positions/${positionId}/analysis`)
    this.removeAnalysis(positionId)
    this.removeAnalysisInterval(positionId)
    this.resetGuestAnalysisCount()
    alert(
      `[${position.startup.name}] ${position.name} 분석 요청이 실패했어요.\n잠시 후 다시 시도해주세요.`,
    )
  }

  removeAnalysisInterval(positionId: number) {
    const intervalId = this.analysisIntervals[positionId]
    if (intervalId) {
      clearInterval(intervalId)
      delete this.analysisIntervals[positionId]
    }
  }

  async analyze(
    position: StartupPositionForAnalysisParams,
    params?: { fileIds: number[] },
    isGuest?: boolean,
  ) {
    await requestToast(
      postStartupPositionAnalyze(position.id, params),
      {
        loading: '분석 요청 중',
        success: isGuest
          ? '분석을 시작했어요. 조금만 기다려주세요.'
          : '분석이 끝나면 카카오 알림을 보내드릴게요.',
      },
      { duration: 5000 },
    )
    this.addAnalysis(position)
    this.addAnalysisInterval(position)
  }
}
